"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { ThemeProvider } from '@mui/material/styles';
import {
    Box,
    Typography,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import theme from '../../theme';
import Toast from '../../components/utils/Toast';
import { useTranslation } from 'react-i18next';
import { fullScreenButtonStyle } from '../../utils/buttonStyles';
import { useAuth } from '@/context/AuthContext'
import axios from 'axios';
import { UserEntityData } from '@/models/entity/userEntity';
import {
    useProfileOptions,
    EXPERIENCE_LEVELS_EN,
    READING_FREQUENCIES_EN,
    USER_GOALS_EN,
    AI_TONE_PREFERENCES_EN,
    getTranslatedValue,
    getEnglishValue
} from '@/constants/profileOptions';

export default function PersonalProfile() {
    const { t } = useTranslation();
    const { user, refreshUserData } = useAuth()
    const profileOptions = useProfileOptions();

    // Get user data from the UserEntity object
    const userData: UserEntityData = user?.toJSON() || {
        id: '',
        firstname: '',
        lastname: '',
        username: '',
        email: '',
        password: '',
        birthdate: null,
        credits: 0,
        lastUpdated: null,
        picture: '',
        gender: '',
        street: '',
        number: '',
        complement: '',
        zipCode: '',
        city: '',
        state: '',
        country: '',
        profile: {},
        onboarding: false
    }

    // Store English values but display translated values
    const [experienceLevel, setExperienceLevel] = useState(userData.profile?.tarot_experience || "")
    const [preferredReadingStyle, setPreferredReadingStyle] = useState(userData.profile?.reading_frequency || "")
    const [primaryLifeFocus, setPrimaryLifeFocus] = useState(userData.profile?.user_goal || "")
    const [intentions, setIntentions] = useState(userData.profile?.ai_tone_preference || "")
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');

    // Update state when user data changes
    useEffect(() => {
        if (userData.profile) {
            // Convert any existing translated values back to English values
            const convertToEnglish = (value: string | undefined, category: 'experience' | 'frequency' | 'goal' | 'tone') => {
                if (!value) return "";
                // Check if the value is already in English
                const englishValues: Record<string, string[]> = {
                    experience: Object.values(EXPERIENCE_LEVELS_EN),
                    frequency: Object.values(READING_FREQUENCIES_EN),
                    goal: Object.values(USER_GOALS_EN),
                    tone: Object.values(AI_TONE_PREFERENCES_EN)
                };

                if (englishValues[category].includes(value)) {
                    return value; // Already in English
                }

                // Convert from translated to English
                return getEnglishValue(value as string, category);
            };

            setExperienceLevel(convertToEnglish(userData.profile.tarot_experience, 'experience'));
            setPreferredReadingStyle(convertToEnglish(userData.profile.reading_frequency, 'frequency'));
            setPrimaryLifeFocus(convertToEnglish(userData.profile.user_goal, 'goal'));
            setIntentions(convertToEnglish(userData.profile.ai_tone_preference, 'tone'));
        }
    }, [userData.profile]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error(t('personalInfo.messages.noAuthToken'));
            }

            const profileData = {
                profile: {
                    ...(userData.profile || {}), // Preserve existing profile data
                    tarot_experience: experienceLevel || undefined,
                    reading_frequency: preferredReadingStyle || undefined,
                    user_goal: primaryLifeFocus || undefined,
                    ai_tone_preference: intentions || undefined
                }
            };

            // Remove undefined fields
            Object.keys(profileData.profile).forEach(key => {
                if (profileData.profile[key as keyof typeof profileData.profile] === undefined) {
                    delete profileData.profile[key as keyof typeof profileData.profile];
                }
            });

            await axios.put(
                `${import.meta.env.VITE_API_URL}/api/user/profile`,
                profileData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Refresh user data from the server to update the UI
            await refreshUserData();

            setToastMessage(t('settings.profileSavedSuccess'));
            setToastSeverity('success');
            setToastOpen(true);

        } catch (error) {
            console.error('Error updating profile:', error);
            setToastMessage(t('personalInfo.messages.failedToUpdateProfile'));
            setToastSeverity('error');
            setToastOpen(true);
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ p: 3, pt: 4, pb: 12, maxWidth: 800, mx: 'auto' }}>
                <form onSubmit={handleSubmit}>
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: '1fr' }}>
                            <FormControl fullWidth>
                                <InputLabel>{t('personalProfile.experienceLevel')}</InputLabel>
                                <Select
                                    value={experienceLevel}
                                    label={t('personalProfile.experienceLevel')}
                                    onChange={(e) => setExperienceLevel(e.target.value)}
                                >
                                    <MenuItem value="">{t('personalProfile.selectExperienceLevel')}</MenuItem>
                                    {Object.values(EXPERIENCE_LEVELS_EN).map((level) => (
                                        <MenuItem key={level} value={level}>
                                            {getTranslatedValue(level, 'experience')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>{t('personalProfile.preferredReadingStyle')}</InputLabel>
                                <Select
                                    value={preferredReadingStyle}
                                    label={t('personalProfile.preferredReadingStyle')}
                                    onChange={(e) => setPreferredReadingStyle(e.target.value)}
                                >
                                    <MenuItem value="">{t('personalProfile.selectPreferredReadingStyle')}</MenuItem>
                                    {Object.values(READING_FREQUENCIES_EN).map((frequency) => (
                                        <MenuItem key={frequency} value={frequency}>
                                            {getTranslatedValue(frequency, 'frequency')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>



                            <FormControl fullWidth>
                                <InputLabel>{t('personalProfile.primaryLifeFocus')}</InputLabel>
                                <Select
                                    value={primaryLifeFocus}
                                    label={t('personalProfile.primaryLifeFocus')}
                                    onChange={(e) => setPrimaryLifeFocus(e.target.value)}
                                >
                                    <MenuItem value="">{t('personalProfile.selectPrimaryLifeFocus')}</MenuItem>
                                    {Object.values(USER_GOALS_EN).map((goal) => (
                                        <MenuItem key={goal} value={goal}>
                                            {getTranslatedValue(goal, 'goal')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>{t('personalProfile.user_goals')}</InputLabel>
                                <Select
                                    value={intentions}
                                    label={t('personalProfile.intentions')}
                                    onChange={(e) => setIntentions(e.target.value)}
                                >
                                    <MenuItem value="">{t('personalProfile.selectIntentions')}</MenuItem>
                                    {Object.values(AI_TONE_PREFERENCES_EN).map((tone) => (
                                        <MenuItem key={tone} value={tone}>
                                            {getTranslatedValue(tone, 'tone')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>



                        </Box>
                    </Box>

                    <Button
                        type="submit"
                        variant="contained"
                        onClick={handleSubmit}
                        fullWidth
                        sx={fullScreenButtonStyle}
                    >
                        {t('settings.saveChanges')}
                    </Button>
                </form>

                <Toast
                    open={toastOpen}
                    message={toastMessage}
                    severity={toastSeverity}
                    onClose={() => setToastOpen(false)}
                />
            </Box>
        </ThemeProvider>
    )
}
